import { DEV_TURNSTILE_BYPASS_TOKEN } from '@ers/shared';
import type { StagePlaintext } from '@ers/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CONFIG } from './config/index';
import { setDefaultPinnedKey } from './config/runtime';
import { StageDecryptionError, loadStage, processStage } from './stages/runner';
import type { StagePayloadParsed } from './stages/schema';
import { RecoveryDisplay, type RecoveryData } from './ui/RecoveryDisplay';
import { TurnstileWidget } from './ui/TurnstileWidget';

export function App(): JSX.Element {
  const devMode = CONFIG.devMode;
  const [stagePayload, setStagePayload] = useState<StagePayloadParsed | null>(null);
  const [stageError, setStageError] = useState<string | null>(null);
  const [answer, setAnswer] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>(
    devMode ? DEV_TURNSTILE_BYPASS_TOKEN : undefined,
  );
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<StagePlaintext | null>(null);
  const stageLoadingRef = useRef(false);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [currentStageUrl, setCurrentStageUrl] = useState<string | null>(null);
  const [currentStageIndex, setCurrentStageIndex] = useState<number>(0);
  const [chainKeyB64, setChainKeyB64] = useState<string | null>(null);
  const [currentParams, setCurrentParams] = useState<{
    saltB64: string;
    ivB64: string;
    kdfParams: {
      hint: string;
      minMemoryMiB: number;
      memoryMiB?: number;
      timeCost?: number;
      parallelism?: number;
    };
  } | null>(null);

  // No persistent storage: do not write stage URL to localStorage

  const workerConfigured = CONFIG.workerBaseUrl.length > 0;
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string>('');
  const [serverTurnstileRequired, setServerTurnstileRequired] = useState<boolean>(true);

  // Turnstile required only if server requires it and we have a site key
  const turnstileRequired =
    !devMode && serverTurnstileRequired && turnstileSiteKey.length > 0;

  const loadStageFromUrl = useCallback(
    async (url: string) => {
      if (!url) {
        setStageError('Failed to load stage.');
        return;
      }
      if (stageLoadingRef.current) {
        return;
      }
      stageLoadingRef.current = true;
      setStageError(null);
      try {
        const payload = await loadStage(url);
        let canonicalUrl = url;
        try {
          canonicalUrl = new URL(url, window.location.href).toString();
        } catch {
          // Failed to resolve canonical URL, use as-is
        }
        setStagePayload(payload);
        setResult(null);
        setAnswer('');
        setCurrentStageUrl(canonicalUrl);
        if (devMode) {
          setTurnstileToken(DEV_TURNSTILE_BYPASS_TOKEN);
        } else {
          setTurnstileToken(undefined);
          if (turnstileRequired) {
            setTurnstileResetKey((key) => key + 1);
          }
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown stage error';
        setStageError(message);
        setStagePayload(null);
        setCurrentStageUrl(null);
      } finally {
        stageLoadingRef.current = false;
      }
    },
    [turnstileRequired],
  );

  useEffect(() => {
    // Fetch setup from worker: initial params, keys, and stage0 token
    // This should only run once on component mount
    void (async () => {
      try {
        const res = await fetch(`${CONFIG.workerBaseUrl.replace(/\/$/, '')}/setup`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error(`setup failed: ${res.status}`);
        const json = await res.json();
        if (!json?.success) throw new Error('invalid setup response');
        const data = json.data as {
          initial: {
            salt: string;
            iv: string;
            kdf_params: {
              hint: string;
              minMemoryMiB: number;
              memoryMiB?: number;
              timeCost?: number;
              parallelism?: number;
            };
            stage0_token: string;
          };
          voprf: { public_key_b64: string };
          turnstile: { site_key: string };
        };
        setTurnstileSiteKey(data.turnstile.site_key);
        setServerTurnstileRequired(data.turnstile.site_key.length > 0);
        setDefaultPinnedKey(data.voprf.public_key_b64);
        setCurrentParams({
          saltB64: data.initial.salt,
          ivB64: data.initial.iv,
          kdfParams: data.initial.kdf_params,
        });
        // Point to worker /stage route instead of public files
        const stage0Url = `${CONFIG.workerBaseUrl}/stage?token=${encodeURIComponent(data.initial.stage0_token)}`;
        await loadStageFromUrl(stage0Url);
      } catch (error) {
        console.error('Setup initialization failed:', error);
        setStageError('Failed to initialize application.');
      }
    })();
  }, []); // Empty dependency array - only run once on mount

  const handleProcessStage = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!stagePayload) {
        setStageError('Load a stage before attempting recovery.');
        return;
      }
      if (turnstileRequired && !turnstileToken) {
        setStageError('Complete the Turnstile challenge.');
        return;
      }
      if (!workerConfigured) {
        setStageError('Worker base URL not configured.');
        return;
      }
      if (!currentStageUrl) {
        setStageError('Stage URL context missing. Reload the stage and try again.');
        return;
      }
      if (currentStageIndex > 0 && !chainKeyB64) {
        setStageError('You must complete previous stages before this one.');
        return;
      }
      setProcessing(true);
      setStageError(null);
      try {
        const outcome = await processStage(stagePayload, {
          answer,
          turnstileToken: turnstileToken || undefined,
          workerBaseUrl: CONFIG.workerBaseUrl,
          currentStageUrl,
          chainKeyPrevB64: chainKeyB64 ?? undefined,
          currentParams: currentParams ?? undefined,
        });
        setResult(outcome.plaintext);

        // Update whether Turnstile is required based on server response.
        // Note: do not clear the site key; we toggle the widget via server flag.
        setServerTurnstileRequired(outcome.turnstileRequired);

        // Reset Turnstile token after successful submission
        if (devMode) {
          setTurnstileToken(DEV_TURNSTILE_BYPASS_TOKEN);
        } else {
          setTurnstileToken(undefined);
          if (turnstileRequired) {
            setTurnstileResetKey((key) => key + 1);
          }
        }
        setChainKeyB64(outcome.chainKeyNextB64);
        // Update next-stage parameters from plaintext
        setCurrentParams(outcome.nextState ?? null);
        if (outcome.nextStageUrl) {
          // Increment stage index for the next stage
          setCurrentStageIndex((prev) => prev + 1);
          await loadStageFromUrl(outcome.nextStageUrl);
        }
      } catch (error) {
        if (error instanceof StageDecryptionError) {
          setStageError('Incorrect answer. Please try again.');
        } else {
          const message =
            error instanceof Error
              ? error.message
              : 'An error occurred. Please try again.';
          setStageError(message);
          // If Turnstile failed (likely due to expired/missing bypass cookie),
          // re-fetch /setup to refresh the site key so the widget reappears.
          if ((error as { code?: string }).code === 'TURNSTILE_FAILED') {
            try {
              const res = await fetch(
                `${CONFIG.workerBaseUrl.replace(/\/$/, '')}/setup`,
                {
                  headers: { Accept: 'application/json' },
                },
              );
              if (res.ok) {
                const json = await res.json();
                if (json?.success && json.data?.turnstile?.site_key !== undefined) {
                  setTurnstileSiteKey(json.data.turnstile.site_key);
                  setServerTurnstileRequired(json.data.turnstile.site_key.length > 0);
                  // Reset widget to prompt for a new token if required
                  setTurnstileToken(undefined);
                  setTurnstileResetKey((key) => key + 1);
                }
              }
            } catch {
              // Ignore setup refresh errors; user can retry
            }
          }
        }
        // Reset Turnstile on error so user can retry
        if (!devMode) {
          setTurnstileToken(undefined);
          if (turnstileRequired) {
            setTurnstileResetKey((key) => key + 1);
          }
        }
      } finally {
        setProcessing(false);
      }
    },
    [
      stagePayload,
      turnstileToken,
      workerConfigured,
      answer,
      loadStageFromUrl,
      turnstileRequired,
      currentStageIndex,
      chainKeyB64,
      currentStageUrl,
      currentParams,
    ],
  );

  const stageHint = useMemo(() => currentParams?.kdfParams.hint ?? '', [currentParams]);

  return (
    <main className="app">
      {stagePayload && !result && (
        <section className="stage-form">
          <p className="hint">{stageHint}</p>
          <form onSubmit={handleProcessStage}>
            <input
              id="answer"
              name="answer"
              type="password"
              value={answer}
              onChange={(event) => setAnswer(event.target.value)}
              placeholder="Enter your answer"
              autoCapitalize="off"
              autoCorrect="off"
              autoComplete="off"
              spellCheck={false}
              disabled={processing}
              autoFocus
            />

            {turnstileRequired && (
              <TurnstileWidget
                siteKey={turnstileSiteKey}
                onToken={setTurnstileToken}
                resetKey={turnstileResetKey}
              />
            )}

            <button
              type="submit"
              disabled={processing || (turnstileRequired && !turnstileToken)}
            >
              {processing ? 'Verifying…' : 'Submit'}
            </button>
          </form>
        </section>
      )}

      {stageError && (
        <section className="error">
          <p>{stageError}</p>
        </section>
      )}

      {result && result.type === 'final' && (
        <RecoveryDisplay data={result.recovery_data as RecoveryData} />
      )}
    </main>
  );
}
